import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBanners } from '../../redux/menu/BannersSlice';
import { FaMapMarkerAlt } from 'react-icons/fa';

const AutoSwipeBanner = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.profile);
  const { banners, status } = useSelector((state) => state.banner);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Extract banners from the new nested structure
  const bannerList = React.useMemo(() => banners?.banner || banners || [], [banners]);

  // Fetch banners when component mounts
  useEffect(() => {
    if (profile?.id) {
      dispatch(fetchBanners(profile.id));
    }
  }, [dispatch, profile?.id]);

  // Auto-swipe functionality
  useEffect(() => {
    if (!bannerList || bannerList.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === bannerList.length - 1 ? 0 : prevIndex + 1
      );
    }, 3500); // Change image every 3.5 seconds for more dynamic feel

    return () => clearInterval(interval);
  }, [bannerList]);



  if (status === 'loading') {
    return (
      <div className="w-full h-32 sm:h-40 md:h-64 lg:h-80 xl:h-96 bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse rounded-lg sm:rounded-xl md:rounded-2xl mx-auto max-w-xs sm:max-w-sm md:max-w-xl lg:max-w-2xl xl:max-w-4xl shadow-lg sm:shadow-xl md:shadow-2xl border-2 sm:border-3 md:border-4" style={{ borderColor: '#e6d4b8' }}>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-600 text-xs sm:text-sm font-medium">Loading banners...</div>
        </div>
      </div>
    );
  }

  if (status === 'failed' || !bannerList || bannerList.length === 0) {
    return (
      <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-xl lg:max-w-2xl xl:max-w-4xl">
        <div className="relative overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg sm:shadow-xl md:shadow-2xl border-2 sm:border-3 md:border-4 backdrop-blur-sm bg-gradient-to-br from-orange-100 to-yellow-100" style={{ borderColor: '#e6d4b8' }}>
          <div className="relative h-32 sm:h-40 md:h-64 lg:h-80 xl:h-96 w-full flex items-center justify-center">
            <div className="text-center px-3 sm:px-4 md:px-6">
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-2 sm:mb-3">🍽️</div>
              <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800 poppins">
                Let's grab some tasty foods
              </div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 mt-1 sm:mt-2">
                Browse our delicious menu below
              </div>
              {profile?.name && (
                <div className="text-xs sm:text-sm md:text-base font-semibold text-primary mt-2 sm:mt-3 poppins">
                  {profile.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-xl lg:max-w-2xl xl:max-w-4xl">
      {/* Store name container - above banner */}
      <div className="flex justify-start mb-1 ">
        <div className="relative bg-gradient-to-r from-red-600 via-red-500 to-red-600 rounded-md px-2 py-1 shadow-md border border-white/30 backdrop-blur-sm">
          <div className="flex items-center justify-center space-x-1">
            <FaMapMarkerAlt className="text-white drop-shadow-lg animate-pulse" style={{ fontSize: '10px' }} />
            <span className="text-white font-bold poppins drop-shadow-lg tracking-wide" style={{ fontSize: '10px' }}>
              {profile?.name || 'Store Name'}
            </span>
            <div className="w-1.5 h-1.5 bg-yellow-300 rounded-full animate-ping"></div>
          </div>
          {/* Decorative elements - smaller */}
          <div className="absolute -top-0.5 -left-0.5 w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse"></div>
          <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse"></div>
          <div className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-yellow-400 rounded-full animate-bounce"></div>
        </div>
      </div>
      
      <div className="relative overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg sm:shadow-xl md:shadow-2xl border-2 sm:border-3 md:border-4 backdrop-blur-sm bg-gradient-to-br from-white/10 to-white/5" style={{ borderColor: '#e6d4b8' }}>
        {/* Banner Images */}
        <div className="relative h-32 sm:h-40 md:h-64 lg:h-80 xl:h-96 w-full">
          {bannerList.map((banner, index) => (
            <div
              key={banner.id || index}
              className={`absolute inset-0 transition-all duration-700 ease-in-out transform ${
                index === currentIndex 
                  ? 'opacity-100 scale-100 translate-x-0' 
                  : 'opacity-0 scale-105 translate-x-4'
              }`}
            >
              <img
                src={banner.DocImage?.imageUrl}
                alt={`Banner ${index + 1}`}
                className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-110"
                style={{ objectFit: 'fill' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {/* Gradient overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
            </div>
          ))}
        </div>
        
        {/* Subtle glow effect */}
        <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl bg-gradient-to-r from-primary/10 via-transparent to-primary/10 opacity-50"></div>
      </div>
    </div>
  );
};

export default AutoSwipeBanner;
